/**
 * use-evotech.ts — Kết nối Evotech (Ứng dụng → App Evotech) + dữ liệu Evotech ở hồ sơ khách.
 * Backend: ecdp_backend_source/src/modules/integrations/evotech/evotech-routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, OAUTH_START_CONFIG } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'

export interface EvotechConnection {
  connected: boolean
  /** eCDP đã đăng ký OAuth với Evotech (có EVOTECH_OAUTH_CLIENT_ID) → hiện nút "Kết nối với Evotech". */
  oauthAvailable?: boolean
  oauthConnected?: boolean
  partnerId?: string | null
  /** Cửa hàng đã chọn trong tài khoản Evotech (null = chưa chọn). */
  shopId?: string | null
  shopName?: string | null
  shopDomain?: string | null
  grantedAt?: string | null
  grantedBy?: string | null
  accessExpiresAt?: string | null
  /** Quyền bị thu hồi / hết hạn → cần cấp lại. */
  needsReauth?: boolean
  enabled?: boolean
  webhookUrl?: string
  /** eCDP đã tự đăng ký endpoint webhook ở Evotech. */
  webhookRegistered?: boolean
  /** Lý do chưa đăng ký được (vd URL chưa công khai) — đồng bộ định kỳ vẫn chạy bù. */
  webhookError?: string | null
  /** false khi PUBLIC_API_URL chưa phải https công khai → Evotech không gửi webhook được. */
  webhookUrlPublic?: boolean
  lastSyncAt?: string | null
  lastError?: string | null
  totalProducts?: number
  /** Đã uỷ quyền cho nhân viên AI đọc dữ liệu Evotech (chỉ đọc). */
  aiAuthorized?: boolean
  aiAuthorizedAt?: string | null
  /** Đã uỷ quyền RIÊNG cho AI tạo đơn nháp — mở rộng thêm trên aiAuthorized, không thay thế. */
  aiOrderAuthorized?: boolean
  aiOrderAuthorizedAt?: string | null
  /** Mẫu link TUỲ CHỈNH (không bắt buộc) — {itemsCode} {itemNo} {parentNo} {code}. Có thì ghi đè link tự động. */
  webappUrlTemplate?: string | null
  miniAppUrlTemplate?: string | null
  /** Link sản phẩm TỰ ĐỘNG theo tên miền / Mini App ID của shop trên Evotech (null = shop chưa có). */
  autoLinks?: { webapp: string | null; miniapp: string | null }
  shopWebDomain?: string | null
  shopMiniAppId?: string | null
  shopLinksSyncedAt?: string | null
}

export interface EvotechStatus {
  connected: boolean
  enabled: boolean
  syncing: boolean
  products: { total: number; byStatus: Record<string, number> }
  orders: number
  /** Đơn chưa gắn được khách eCDP (khách vãng lai / khách chưa đồng bộ). */
  ordersWithoutContact?: number
  linkedContacts: number
  /** Khách được tạo mới từ đồng bộ Evotech. */
  createdFromEvotech?: number
  customersSyncedFrom?: string | null
  lastSyncAt: string | null
  lastFullSyncAt: string | null
  lastWebhookAt: string | null
  lastWebhookEvent: string | null
  lastError: string | null
  lastErrorAt: string | null
}

export interface EvotechMember {
  userNo: number
  memberCode: string | null
  fullname: string | null
  phone: string | null
  level: string | null
  nextLevel: string | null
  remainingSpend: number | null
  point: number | null
  totalSpent: number | null
  tags: string[]
  vouchersCount: number | null
}

export type EvotechMemberResult =
  | { configured: false }
  | { configured: true; linked: true; member: EvotechMember }
  | { configured: true; linked: false; reason: 'no_phone' | 'not_found' | 'ambiguous'; candidates?: EvotechMember[] }

export interface ExternalOrderItem {
  itemNo: number | null
  sku: string | null
  /** Tên kèm biến thể: "Áo thun - Đỏ / M". */
  name: string
  /** Tên sản phẩm gốc (không kèm biến thể). */
  productName?: string | null
  quantity: number
  /** Đơn giá bán. */
  price: number | null
  total: number | null
  isGift: boolean
  itemsCode?: string | null
  barcode?: string | null
  image?: string | null
  /** Giá niêm yết (giá gạch) khi cao hơn giá bán. */
  listPrice?: number | null
  /** "Màu: Đỏ · Size: M". */
  variant?: string | null
  /** Link sản phẩm (Webapp / Mini App) dựng ở máy chủ. */
  link?: string | null
}

export interface ExternalOrderDetails {
  typePay: string | null
  delivery: string | null
  origin: string | null
  branchName: string | null
  subtotal: number | null
  shippingFee: number | null
  discount: number | null
  /** Trừ điểm: số tiền + số điểm đã dùng. */
  pointDiscount?: number | null
  pointsUsed?: number | null
  tax: number | null
  notes: string | null
  voucherCodes: string[]
  shipTo: { name: string | null; phone: string | null; address: string | null } | null
  shippingCarrier: string | null
}

export interface ExternalOrder {
  id: string
  source: string
  externalId: string
  status: string | null
  payStatus: string | null
  transportStatus: string | null
  total: number
  currency: string
  items: ExternalOrderItem[]
  /** Thanh toán, giao nhận, giảm giá, địa chỉ giao… (null với nguồn chưa hỗ trợ). */
  details?: ExternalOrderDetails | null
  placedAt: string | null
  updatedAtSource: string | null
}

const KEY = ['evotech'] as const

export function useEvotechConnection(enabled = true) {
  return useQuery<EvotechConnection>({
    queryKey: [...KEY, 'connection'],
    queryFn: async () => (await api.get('/integrations/evotech')).data,
    enabled,
  })
}

export function useEvotechStatus(enabled = true) {
  return useQuery<EvotechStatus>({
    queryKey: [...KEY, 'status'],
    queryFn: async () => (await api.get('/integrations/evotech/status')).data,
    enabled,
    // Đang đồng bộ → cập nhật số liệu mỗi 5 giây.
    refetchInterval: (q) => (q.state.data?.syncing ? 5000 : false),
  })
}

export { EVOTECH_OAUTH_MESSAGE, type EvotechOAuthMessage } from '@/lib/evotech-oauth-return'

/**
 * Bắt đầu uỷ quyền: chuyển CHÍNH TAB này sang trang đồng ý của Evotech; xong Evotech → máy chủ eCDP → quay về
 * /integrations/apps/evotech?evotech_connected=1 ngay tab cũ và trang hiện thông báo (không còn popup — popup bị cắt `window.opener`
 * khi đi qua Evotech, trong PWA / màn hẹp thì mở thành trang "Chức năng dùng trên máy tính" thay vì tự đóng; sửa 15/10/2026).
 * Request `start` gửi kèm credentials để trình duyệt giữ cookie `evotech_oauth_nonce` (xem OAUTH_START_CONFIG).
 */
export function useStartEvotechOAuth() {
  return useMutation({
    mutationFn: async () => (await api.post<{ authorizeUrl: string }>('/integrations/evotech/oauth/start', { popup: false }, OAUTH_START_CONFIG)).data.authorizeUrl,
    onSuccess: (authorizeUrl) => { window.location.assign(authorizeUrl) },
  })
}

export function useDisconnectEvotechOAuth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.delete<{ ok: boolean; revokedAtEvotech: boolean }>('/integrations/evotech/oauth')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export interface EvotechShop {
  shopId: string
  name: string
  domain: string | null
}

/** Cửa hàng của tài khoản Evotech đã uỷ quyền (lấy bằng token partner). */
export function useEvotechShops(enabled: boolean) {
  return useQuery<{ shops: EvotechShop[]; selectedShopId: string | null }>({
    queryKey: [...KEY, 'shops'],
    queryFn: async () => (await api.get('/integrations/evotech/oauth/shops')).data,
    enabled,
    retry: false,
  })
}

export function useSelectEvotechShop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (shopId: string) => (await api.put('/integrations/evotech/oauth/shop', { shopId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useSaveEvotech() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { enabled?: boolean; reregisterWebhook?: boolean; refreshLinks?: boolean }) =>
      (await api.put<EvotechConnection>('/integrations/evotech', body)).data,
    onSuccess: (_d, body) => {
      qc.invalidateQueries({ queryKey: KEY })
      if (body.refreshLinks) qc.invalidateQueries({ queryKey: ['products', 'picker-sources'] })
    },
  })
}

/** Lưu mẫu link sản phẩm (Webapp / Mini App). Chuỗi rỗng = xoá mẫu. */
export function useSaveEvotechLinkTemplates() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { webappUrlTemplate?: string; miniAppUrlTemplate?: string }) =>
      (await api.put<EvotechConnection>('/integrations/evotech/link-templates', body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['products', 'picker-sources'] })
    },
  })
}

export function useDisconnectEvotech() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.delete<{ ok: boolean; archivedProducts?: number }>('/integrations/evotech')).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

/** Uỷ quyền / thu hồi uỷ quyền cho nhân viên AI đọc dữ liệu Evotech. */
export function useAuthorizeEvotechAi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (on: boolean) => (on
      ? (await api.post<EvotechConnection>('/integrations/evotech/ai-authorize')).data
      : (await api.delete<EvotechConnection>('/integrations/evotech/ai-authorize')).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['ai'] })
    },
  })
}

/** Uỷ quyền RIÊNG cho AI tạo đơn nháp — cần bật "Uỷ quyền đọc dữ liệu" (useAuthorizeEvotechAi) trước. */
export function useAuthorizeEvotechAiOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (on: boolean) => (on
      ? (await api.post<EvotechConnection>('/integrations/evotech/ai-order-authorize')).data
      : (await api.delete<EvotechConnection>('/integrations/evotech/ai-order-authorize')).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['ai'] })
    },
  })
}

export function useSyncEvotech() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post('/integrations/evotech/sync')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'status'] }),
  })
}

// ── Hồ sơ khách ──────────────────────────────────────────────────────

export function useContactEvotech(contactId?: string) {
  return useQuery<EvotechMemberResult>({
    queryKey: [...KEY, 'member', contactId],
    queryFn: async () => (await api.get(`/contacts/${contactId}/evotech`)).data,
    enabled: !!contactId,
    staleTime: 60_000,
    retry: false,
  })
}

export function useLinkEvotech(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (userNo: number) => (await api.post(`/contacts/${contactId}/evotech/link`, { userNo })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'member', contactId] })
      qc.invalidateQueries({ queryKey: ['contact-orders', contactId] })
    },
  })
}

export function useUnlinkEvotech(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.delete(`/contacts/${contactId}/evotech/link`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'member', contactId] }),
  })
}

export function useContactOrders(contactId?: string, source?: string) {
  return useQuery<{ total: number; orders: ExternalOrder[] }>({
    queryKey: ['contact-orders', contactId, source ?? 'all'],
    queryFn: async () => (await api.get(`/contacts/${contactId}/orders`, { params: source ? { source } : undefined })).data,
    enabled: !!contactId,
    staleTime: 30_000,
  })
}

// ── Nhãn trạng thái đơn (Evotech enum → tiếng Việt) ──────────────────

type Tone = 'success' | 'warning' | 'destructive' | 'secondary' | 'default'

export const ORDER_STATUS: Record<string, [string, Tone]> = {
  get Pending(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Pending'), 'warning'] },
  get Verified(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Verified'), 'default'] },
  get WaitingForDelivery(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.WaitingForDelivery'), 'default'] },
  get Delivering(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Delivering'), 'default'] },
  get Success(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Success'), 'success'] },
  get Failed(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Failed'), 'destructive'] },
  get Cancelled(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Cancelled'), 'destructive'] },
  get Refund(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.Refund'), 'secondary'] },
  get WaitingForReview(): [string, Tone] { return [i18n.t('chanEvotech:hooks.orderStatus.WaitingForReview'), 'secondary'] },
}

export const PAY_STATUS: Record<string, [string, Tone]> = {
  get NotPaid(): [string, Tone] { return [i18n.t('chanEvotech:hooks.payStatus.NotPaid'), 'warning'] },
  get Paid(): [string, Tone] { return [i18n.t('chanEvotech:hooks.payStatus.Paid'), 'success'] },
  get Refund(): [string, Tone] { return [i18n.t('chanEvotech:hooks.payStatus.Refund'), 'secondary'] },
}

export const TRANSPORT_STATUS: Record<string, [string, Tone]> = {
  get Created(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Created'), 'secondary'] },
  get Verified(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Verified'), 'secondary'] },
  get WaitingForDelivery(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.WaitingForDelivery'), 'secondary'] },
  get Delivered(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Delivered'), 'default'] },
  get Transporting(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Transporting'), 'default'] },
  get Delivering(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Delivering'), 'default'] },
  get Success(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Success'), 'success'] },
  get Waiting(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Waiting'), 'secondary'] },
  get Refunding(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Refunding'), 'warning'] },
  get Refunded(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Refunded'), 'secondary'] },
  get Cancel(): [string, Tone] { return [i18n.t('chanEvotech:hooks.transportStatus.Cancel'), 'destructive'] },
}

export function statusLabel(map: Record<string, [string, Tone]>, v: string | null): [string, Tone] | null {
  if (!v) return null
  return map[v] ?? [v, 'secondary']
}

/** Hình thức thanh toán của đơn Evotech (TypePaymentEnum). */
export const TYPE_PAY_LABEL: Record<string, string> = {
  get COD() { return i18n.t('chanEvotech:hooks.typePay.COD') },
  get Cash() { return i18n.t('chanEvotech:hooks.typePay.Cash') },
  get Transfer() { return i18n.t('chanEvotech:hooks.typePay.Transfer') },
  get Sepay() { return i18n.t('chanEvotech:hooks.typePay.Sepay') },
  get Vnpay() { return i18n.t('chanEvotech:hooks.typePay.Vnpay') },
  get Momo() { return i18n.t('chanEvotech:hooks.typePay.Momo') },
  get Zalo() { return i18n.t('chanEvotech:hooks.typePay.Zalo') },
  get Points() { return i18n.t('chanEvotech:hooks.typePay.Points') },
  get Other() { return i18n.t('chanEvotech:hooks.typePay.Other') },
}

/** Hình thức nhận hàng (TypeDeliveryEnum). */
export const DELIVERY_LABEL: Record<string, string> = {
  get InShop() { return i18n.t('chanEvotech:hooks.delivery.InShop') },
  get ExpressDelivery() { return i18n.t('chanEvotech:hooks.delivery.ExpressDelivery') },
}

/** Kênh tạo đơn (TypeOriginEnum). */
export const ORDER_ORIGIN_LABEL: Record<string, string> = {
  get WebPartner() { return i18n.t('chanEvotech:hooks.origin.WebPartner') },
  get ZaloMiniApp() { return i18n.t('chanEvotech:hooks.origin.ZaloMiniApp') },
  get WebApp() { return i18n.t('chanEvotech:hooks.origin.WebApp') },
  get Pos() { return i18n.t('chanEvotech:hooks.origin.Pos') },
  get Affiliation() { return i18n.t('chanEvotech:hooks.origin.Affiliation') },
  get Offline() { return i18n.t('chanEvotech:hooks.origin.Offline') },
  get Other() { return i18n.t('chanEvotech:hooks.origin.Other') },
}
